import { MenuPlacement } from 'react-select';
import {
  AsyncCreatableSelectProps,
  AsyncSelectProps,
  CreatableSelectProps,
  SelectProps,
} from '../types';
import { findOption } from './utils';
import {
  GenericAsyncCreatableSelect,
  GenericAsyncSelect,
  GenericCreatableSelect,
  GenericSelect,
} from './GenericSelect';
import { selectOptionOperations } from '../selectOptionsOperations';
import { Control } from './reactSelectComponents/Control';
import { CSSProperties, useMemo } from 'react';
import { FormSize } from '@ardoq/forms';

// eslint-disable-next-line eqeqeq
const isNil = <T,>(value: T): value is T => value == null;

const composeProps = <TValue,>(
  props: SelectProps<TValue>,
  isCreatable = false
): Partial<SelectProps<TValue>> => {
  const {
    value,
    options,
    defaultValue,
    onValueChange,
    onChange,
    menuPlacement,
    minMenuHeight,
    allowNullOptionValue,
    preventBodyScroll = true,
    onBackdropClick,
  } = props;
  const isControlled = 'value' in props;
  const flatOptions =
    selectOptionOperations.optionsOrGroupsToFlatOptions(options);
  return {
    value:
      findOption(
        value,
        flatOptions,
        isCreatable,
        isControlled,
        allowNullOptionValue
      ) || (defaultValue ? undefined : null),
    defaultValue: findOption(
      defaultValue,
      flatOptions,
      isCreatable,
      allowNullOptionValue
    ),
    menuPlacement: menuPlacement ?? ('auto' as MenuPlacement),
    minMenuHeight: minMenuHeight ?? 300,
    onChange: option => {
      onChange?.(option);
      const value = option?.value;
      onValueChange?.(isNil(value) ? null : value);
    },
    preventBodyScroll,
    onBackdropClick,
  };
};

export const Select = <TValue,>(props: SelectProps<TValue>) => (
  <GenericSelect {...props} {...composeProps(props)} />
);

export const CreatableSelect = <TValue,>(
  props: CreatableSelectProps<TValue>
) => <GenericCreatableSelect {...props} {...composeProps(props, true)} />;

export const AsyncSelect = <TValue,>(props: AsyncSelectProps<TValue>) => (
  <GenericAsyncSelect {...props} {...composeProps(props)} />
);

export const AsyncCreatableSelect = <TValue,>(
  props: AsyncCreatableSelectProps<TValue>
) => <GenericAsyncCreatableSelect {...props} {...composeProps(props, true)} />;

type CellSelectProps<TValue> = SelectProps<TValue> & { style?: CSSProperties };

type CellAsyncSelectProps<TValue> = AsyncSelectProps<TValue> & {
  style?: CSSProperties;
};

const cellSelectControlDefaultStyle: CSSProperties = {
  height: '100%',
  cursor: 'pointer',
  backgroundColor: 'transparent',
  boxShadow: 'none',
};

const getCellSelectProps = <TValue,>(
  controlStyle?: CSSProperties,
  customStyles?: SelectProps<TValue>['styles']
) => ({
  formSize: FormSize.FULL,
  components: {
    Control: ({ children, ...controlProps }: any) => (
      <Control
        {...controlProps}
        style={{
          ...cellSelectControlDefaultStyle,
          ...controlStyle,
        }}
      >
        {children}
      </Control>
    ),
  },
  styles: {
    menu: (provided: any) => ({
      ...provided,
      minWidth: 'max(240px, 100%)',
      width: 'auto',
    }),
    ...customStyles,
  },
});

export const CellSelect = <TValue,>({
  style: controlStyle,
  styles: customStyles,
  ...props
}: CellSelectProps<TValue>) => {
  const cellSelectProps = useMemo(
    () => getCellSelectProps<TValue>(controlStyle, customStyles),
    [controlStyle, customStyles]
  );
  return <Select {...props} {...cellSelectProps} />;
};

export const CellAsyncSelect = <TValue,>({
  style: controlStyle,
  styles: customStyles,
  ...props
}: CellAsyncSelectProps<TValue>) => {
  const cellSelectProps = useMemo(
    () => getCellSelectProps<TValue>(controlStyle, customStyles),
    [controlStyle, customStyles]
  );
  return <AsyncSelect {...props} {...cellSelectProps} />;
};
